import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { BrainCard } from '../components/brains/BrainCard'
import { ErrorState } from '../components/ui'
import { usePageTitle } from '../hooks/usePageTitle'
import { fetchMountedBrains, fetchBrainCards, remountBrain, getCsrfToken } from '../api/brain'
import type { MountedBrain, AgentCard } from '../types/brain'

type BrainsTab = 'mounted' | 'chat'

const TABS: Array<{ key: BrainsTab; label: string }> = [
  { key: 'mounted', label: 'Mounted' },
  { key: 'chat', label: 'Chat' },
]

const TAB_KEYS = TABS.map(t => t.key)

const AUTO_REFRESH_MS = 60_000

export function Brains() {
  usePageTitle('Brains')
  const [activeTab, setActiveTab] = useState<BrainsTab>('mounted')
  const [selectedBrainId, setSelectedBrainId] = useState<string | null>(null)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  const [mounts, setMounts] = useState<MountedBrain[]>([])
  const [cards, setCards] = useState<AgentCard[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cardsWarning, setCardsWarning] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    setError(null)
    setCardsWarning(null)
    try {
      const [mountsResult, cardsResult] = await Promise.allSettled([
        fetchMountedBrains(),
        fetchBrainCards('decisive'),
      ])

      if (mountsResult.status === 'rejected') {
        throw mountsResult.reason
      }

      setMounts(mountsResult.value)

      if (cardsResult.status === 'fulfilled') {
        setCards(cardsResult.value)
      } else {
        setCards([])
        const msg = cardsResult.reason instanceof Error ? cardsResult.reason.message : 'Failed to load brain cards'
        setCardsWarning(`Brain cards unavailable: ${msg}`)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load brains'
      // Match on status codes embedded in the standard error format "Request failed (NNN)"
      const statusMatch = msg.match(/Request failed \((\d+)\)/)
      const status = statusMatch ? parseInt(statusMatch[1], 10) : 0
      if (status === 501) {
        setError('Brains panel requires a locally-running agentbootup daemon. Set VITE_ENABLE_BRAINS=true and AGENTBOOTUP_DAEMON_URL in functions/.dev.vars, then run wrangler pages dev.')
      } else if (status === 502 || status === 504) {
        setError('agentbootup daemon is unreachable. Is it running on :8765?')
      } else {
        console.error('[Brains] loadData error:', err)
        setError('Failed to load brains. Check the console for details.')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
    const interval = setInterval(loadData, AUTO_REFRESH_MS)
    return () => clearInterval(interval)
  }, [loadData])

  const handleChat = (brainId: string) => {
    setSelectedBrainId(brainId)
    setActiveTab('chat')
  }

  const handleRemount = useCallback(async (brainId: string) => {
    await remountBrain(brainId)
    loadData().catch(console.error)
  }, [loadData])

  const cardMap = useMemo(() => new Map(cards.map(c => [c.brain_id, c])), [cards])

  if (loading) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-[24px] font-semibold tracking-tight leading-[1.2]">Brains</h1>
          <p className="text-swiss-gray-500 mt-2 text-[14px]">
            Mounted brains in the decisive environment.
          </p>
        </div>
        <BrainsSkeleton />
      </div>
    )
  }

  if (error) {
    return (
      <ErrorState
        title="Brains Unavailable"
        message={error}
        onRetry={() => { setLoading(true); loadData() }}
      />
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-[24px] font-semibold tracking-tight leading-[1.2]">Brains</h1>
        <p className="text-swiss-gray-500 mt-2 text-[14px]">
          Mounted brains in the decisive environment.
        </p>
      </div>

      {cardsWarning && (
        <div className="mb-6 border border-swiss-gray-200 bg-swiss-off-white px-4 py-3 text-[13px] text-swiss-gray-600">
          {cardsWarning}
        </div>
      )}

      <div className="flex gap-0 border-b border-swiss-gray-200 mb-6" role="tablist" aria-label="Brains tabs">
        {TABS.map((tab, i) => {
          const isActive = activeTab === tab.key
          return (
            <button
              key={tab.key}
              id={`brains-tab-${tab.key}`}
              ref={el => { tabRefs.current[i] = el }}
              role="tab"
              aria-selected={isActive}
              aria-controls="brains-tabpanel"
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActiveTab(tab.key)}
              onKeyDown={(e) => {
                const idx = TAB_KEYS.indexOf(tab.key)
                let newIdx: number | null = null
                if (e.key === 'ArrowRight') newIdx = (idx + 1) % TAB_KEYS.length
                if (e.key === 'ArrowLeft') newIdx = (idx - 1 + TAB_KEYS.length) % TAB_KEYS.length
                if (newIdx !== null) {
                  setActiveTab(TAB_KEYS[newIdx])
                  tabRefs.current[newIdx]?.focus()
                }
              }}
              className={`px-4 py-3 text-[13px] font-medium cursor-pointer transition-colors duration-150 border-b-2 -mb-px ${
                isActive
                  ? 'border-swiss-black text-swiss-black'
                  : 'border-transparent text-swiss-gray-500 hover:text-swiss-black'
              }`}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      <div id="brains-tabpanel" role="tabpanel" aria-labelledby={`brains-tab-${activeTab}`}>
        {activeTab === 'mounted' && (
          <MountedTab mounts={mounts} cardMap={cardMap} onChat={handleChat} onRemount={handleRemount} />
        )}
        {activeTab === 'chat' && (
          <ChatTab mounts={mounts} selectedBrainId={selectedBrainId} />
        )}
      </div>
    </div>
  )
}

// --- Mounted tab ---

interface MountedTabProps {
  mounts: MountedBrain[]
  cardMap: Map<string, AgentCard>
  onChat: (brainId: string) => void
  onRemount: (brainId: string) => Promise<void>
}

function MountedTab({ mounts, cardMap, onChat, onRemount }: MountedTabProps) {
  if (mounts.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-[14px] text-swiss-gray-500 mb-3">
          No brains mounted in the decisive environment.
        </p>
        <p className="text-[13px] font-mono text-swiss-gray-400">
          agentbootup install &lt;brain&gt; --env-config decisive-env.json
        </p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {mounts.map((mount) => (
        <BrainCard
          key={mount.brain_id}
          mount={mount}
          card={cardMap.get(mount.brain_id)}
          onChat={onChat}
          onRemount={onRemount}
        />
      ))}
    </div>
  )
}

// --- Skeleton loading state ---

function BrainsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="border border-swiss-gray-200 p-5 flex flex-col gap-4 animate-pulse">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-2">
              <div className="h-4 bg-swiss-gray-100 rounded w-24" />
              <div className="flex gap-2">
                <div className="h-4 bg-swiss-gray-100 rounded w-16" />
                <div className="h-4 bg-swiss-gray-100 rounded w-20" />
              </div>
            </div>
          </div>
          <div className="h-3 bg-swiss-gray-100 rounded w-3/4" />
          <div className="flex gap-4">
            <div className="h-3 bg-swiss-gray-100 rounded w-14" />
            <div className="h-3 bg-swiss-gray-100 rounded w-20" />
          </div>
          <div className="flex gap-2 pt-1">
            <div className="h-8 bg-swiss-gray-100 rounded w-16" />
          </div>
        </div>
      ))}
    </div>
  )
}

// --- Chat tab ---

interface OrchestrateEvent {
  type: string
  turn?: number
  output?: string
  decision?: string
  confidence?: number
  escalationKind?: string
  message?: string
}

interface ChatMessage {
  id: string
  role: 'user' | 'agent'
  text: string
  eventType?: string
  turn?: number
}

interface ChatThread {
  id: string
  brain_id: string
  title: string | null
  created_at: string
  updated_at: string
}

interface ChatTabProps {
  mounts: MountedBrain[]
  selectedBrainId: string | null
}

function formatThreadDate(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' })
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function ChatTab({ mounts, selectedBrainId: initialBrainId }: ChatTabProps) {
  const [brainId, setBrainId] = useState<string>(
    initialBrainId ?? mounts[0]?.brain_id ?? ''
  )
  const [goal, setGoal] = useState('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Thread state
  const [threads, setThreads] = useState<ChatThread[]>([])
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
  const [threadsLoading, setThreadsLoading] = useState(false)

  const bottomRef = useRef<HTMLDivElement | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Sync brainId when initialBrainId changes (clicking Chat → from a card)
  useEffect(() => {
    if (initialBrainId) setBrainId(initialBrainId)
  }, [initialBrainId])

  // Abort in-flight fetch on unmount
  useEffect(() => () => { abortRef.current?.abort() }, [])

  // Auto-scroll to bottom when messages update
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Load thread list whenever brain selection changes
  useEffect(() => {
    if (!brainId) return
    const ac = new AbortController()
    setThreadsLoading(true)
    setActiveThreadId(null)
    setMessages([])
    fetch(`/api/brains/threads?brain_id=${encodeURIComponent(brainId)}`, {
      credentials: 'include',
      signal: ac.signal,
    })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((data: { threads: ChatThread[] }) => { if (!ac.signal.aborted) setThreads(data.threads ?? []) })
      .catch(err => { if (!ac.signal.aborted) console.error('[ChatTab] failed to load threads:', err) })
      .finally(() => { if (!ac.signal.aborted) setThreadsLoading(false) })
    return () => ac.abort()
  }, [brainId])

  // Load messages when switching to an existing thread
  const handleSelectThread = useCallback(async (threadId: string) => {
    if (threadId === activeThreadId) return
    setActiveThreadId(threadId)
    setMessages([])
    setError(null)
    try {
      const res = await fetch(`/api/brains/threads/${threadId}`, { credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json() as { thread: ChatThread; messages: ChatMessage[] }
      setMessages(data.messages ?? [])
    } catch (err) {
      console.error('[ChatTab] failed to load thread:', err)
      setError('Failed to load conversation history.')
    }
  }, [activeThreadId])

  const handleNewChat = useCallback(() => {
    setActiveThreadId(null)
    setMessages([])
    setError(null)
    setGoal('')
  }, [])

  // Save messages to the active thread after a turn completes
  const saveTurn = useCallback(async (threadId: string, turnMessages: ChatMessage[]) => {
    const payload = turnMessages.map(m => ({
      role: m.role,
      text: m.text,
      event_type: m.eventType ?? null,
      turn: m.turn ?? null,
    }))
    try {
      await fetch(`/api/brains/threads/${threadId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({ messages: payload }),
      })
      // Refresh thread list so updated_at bubbles the active thread to top
      const r = await fetch(`/api/brains/threads?brain_id=${encodeURIComponent(brainId)}`, {
        credentials: 'include',
      })
      if (r.ok) {
        const data = await r.json() as { threads: ChatThread[] }
        setThreads(data.threads ?? [])
      }
    } catch (err) {
      console.error('[ChatTab] failed to save turn:', err)
      // Non-fatal — messages already visible in UI
    }
  }, [brainId])

  const handleSend = useCallback(async () => {
    if (!goal.trim() || sending) return
    setError(null)
    setSending(true)

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      text: goal.trim(),
    }
    setMessages(prev => [...prev, userMsg])
    const sentGoal = goal.trim()
    setGoal('')

    // Create a new thread if this is the first message in a new chat
    let threadId = activeThreadId
    if (!threadId) {
      try {
        const res = await fetch('/api/brains/threads', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
          body: JSON.stringify({ brain_id: brainId, title: sentGoal }),
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json() as { thread: ChatThread }
        threadId = data.thread.id
        setActiveThreadId(threadId)
        setThreads(prev => [data.thread, ...prev])
      } catch (err) {
        console.error('[ChatTab] failed to create thread:', err)
        // Continue without persistence rather than blocking the user
      }
    }

    abortRef.current = new AbortController()
    const turnAgentMessages: ChatMessage[] = []

    try {
      const res = await fetch('/api/brains/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        body: JSON.stringify({
          brain_id: brainId,
          goal: sentGoal,
          approval_mode: 'confidence',
          confidence_threshold: 0.8,
        }),
        signal: abortRef.current.signal,
      })

      if (!res.ok) {
        const text = await res.text()
        throw new Error(`Request failed (${res.status})${text ? ': ' + text : ''}`)
      }

      if (!res.body) throw new Error('Empty response body from chat endpoint')
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let streamDone = false

      const pushAgentMsg = (msg: ChatMessage) => {
        setMessages(prev => [...prev, msg])
        turnAgentMessages.push(msg)
      }

      outer: while (!streamDone) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const parts = buffer.split('\n\n')
        buffer = parts.pop() ?? ''

        for (const part of parts) {
          const line = part.replace(/^data:\s*/, '').trim()
          if (!line) continue
          let evt: OrchestrateEvent
          try { evt = JSON.parse(line) } catch { continue }

          if (evt.type === 'done') { streamDone = true; break outer }

          if (evt.type === 'error') {
            setError(evt.message ?? 'Agent error')
            streamDone = true
            break outer
          }

          if (evt.type === 'turn_complete' || evt.type === 'complete') {
            const text = evt.output ?? evt.decision ?? `[${evt.type}]`
            pushAgentMsg({
              id: `a-${Date.now()}-${Math.random()}`,
              role: 'agent',
              text,
              eventType: evt.type,
              turn: evt.turn,
            })
          } else if (evt.type === 'escalation') {
            const badge = `[escalation: ${evt.escalationKind ?? 'unknown'} — confidence ${((evt.confidence ?? 0) * 100).toFixed(0)}%]`
            if (evt.output?.trim()) {
              pushAgentMsg({
                id: `esc-out-${Date.now()}-${Math.random()}`,
                role: 'agent',
                text: evt.output.trim(),
                turn: evt.turn,
              })
            }
            pushAgentMsg({
              id: `esc-${Date.now()}-${Math.random()}`,
              role: 'agent',
              text: badge,
              eventType: 'escalation',
              turn: evt.turn,
            })
          }
        }
      }

      // Flush remaining buffer
      const tail = buffer.replace(/^data:\s*/, '').trim()
      if (tail) {
        try {
          const evt: OrchestrateEvent = JSON.parse(tail)
          if (evt.type === 'error') setError(evt.message ?? 'Agent error')
          else if (evt.type === 'turn_complete' || evt.type === 'complete') {
            const text = evt.output ?? evt.decision ?? `[${evt.type}]`
            pushAgentMsg({
              id: `a-${Date.now()}-${Math.random()}`,
              role: 'agent',
              text,
              eventType: evt.type,
              turn: evt.turn,
            })
          }
        } catch { /* incomplete chunk — discard */ }
      }

      // Persist this turn (user message + all agent messages)
      if (threadId) {
        await saveTurn(threadId, [userMsg, ...turnAgentMessages])
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return
      const msg = err instanceof Error ? err.message : 'Failed to send'
      setError(msg)
    } finally {
      setSending(false)
      abortRef.current = null
    }
  }, [brainId, goal, sending, activeThreadId, saveTurn])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend()
  }, [handleSend])

  if (mounts.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-[14px] text-swiss-gray-500 mb-3">
          No brains mounted. Install a brain first.
        </p>
        <p className="text-[13px] font-mono text-swiss-gray-400">
          agentbootup install &lt;brain&gt; --env-config decisive-env.json
        </p>
      </div>
    )
  }

  return (
    <div className="flex gap-6">
      {/* Thread sidebar */}
      <div className="w-48 shrink-0 flex flex-col gap-2">
        {/* Brain selector */}
        <select
          value={brainId}
          onChange={e => setBrainId(e.target.value)}
          disabled={sending}
          aria-label="Select brain"
          className="text-[12px] border border-swiss-gray-200 px-2 py-1.5 bg-white text-swiss-black focus:outline-none focus:border-swiss-gray-500 w-full"
        >
          {mounts.map(m => (
            <option key={m.brain_id} value={m.brain_id}>{m.brain_id}</option>
          ))}
        </select>

        <button
          onClick={handleNewChat}
          disabled={sending}
          className="text-[12px] font-medium px-3 py-1.5 border border-swiss-gray-200 text-swiss-black cursor-pointer hover:bg-swiss-gray-100 disabled:opacity-40 text-left"
        >
          + New chat
        </button>

        <div className="flex flex-col gap-0.5 mt-1">
          {threadsLoading && (
            <p className="text-[11px] text-swiss-gray-400 px-1">Loading…</p>
          )}
          {!threadsLoading && threads.length === 0 && (
            <p className="text-[11px] text-swiss-gray-400 px-1">No conversations yet</p>
          )}
          {threads.map(t => (
            <button
              key={t.id}
              onClick={() => handleSelectThread(t.id)}
              className={`text-left px-2 py-1.5 text-[12px] cursor-pointer transition-colors duration-100 leading-snug ${
                t.id === activeThreadId
                  ? 'bg-swiss-black text-swiss-white'
                  : 'text-swiss-black hover:bg-swiss-gray-100'
              }`}
            >
              <span className="block truncate">{t.title ?? 'Untitled'}</span>
              <span className={`block text-[10px] mt-0.5 ${t.id === activeThreadId ? 'text-swiss-gray-300' : 'text-swiss-gray-400'}`}>
                {formatThreadDate(t.updated_at)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col gap-4 min-w-0">
        {/* Message stream */}
        {messages.length > 0 ? (
          <div className="border border-swiss-gray-200 p-4 flex flex-col gap-3 max-h-[480px] overflow-y-auto">
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`flex flex-col gap-0.5 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
              >
                {msg.turn !== undefined && (
                  <span className="text-[10px] text-swiss-gray-400">turn {msg.turn}</span>
                )}
                <div
                  className={`text-[13px] px-3 py-2 max-w-[85%] whitespace-pre-wrap leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-swiss-black text-swiss-white'
                      : msg.eventType === 'escalation'
                      ? 'bg-amber-50 text-amber-800 border border-amber-200'
                      : 'bg-swiss-gray-100 text-swiss-black'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="text-[12px] text-swiss-gray-400 italic">Agent thinking…</div>
            )}
            <div ref={bottomRef} />
          </div>
        ) : (
          !activeThreadId && (
            <div className="border border-swiss-gray-200 border-dashed p-8 text-center">
              <p className="text-[13px] text-swiss-gray-400">
                Ask this brain anything — responses stream live
              </p>
            </div>
          )
        )}

        {/* Error */}
        {error && (
          <p role="alert" className="text-[12px] text-red-600">{error}</p>
        )}

        {/* Input */}
        <div className="flex gap-2">
          <label htmlFor="chat-goal" className="sr-only">Goal</label>
          <textarea
            id="chat-goal"
            value={goal}
            onChange={e => setGoal(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
            placeholder="Ask this brain anything… (⌘/Ctrl+↵ to send)"
            rows={3}
            className="flex-1 text-[13px] border border-swiss-gray-200 px-3 py-2 resize-none focus:outline-none focus:border-swiss-gray-500 disabled:opacity-50"
          />
          <button
            onClick={handleSend}
            disabled={sending || !goal.trim()}
            className="text-[13px] font-medium px-4 py-2 bg-swiss-black text-swiss-white cursor-pointer transition-colors duration-150 hover:bg-swiss-gray-800 disabled:opacity-40 disabled:cursor-not-allowed self-end"
          >
            {sending ? 'Sending…' : 'Send →'}
          </button>
        </div>
        <p className="text-[11px] text-swiss-gray-400">⌘/Ctrl+↵ to send · agent responses stream live</p>
      </div>
    </div>
  )
}
